import { Store } from '@ngrx/store';
import { ICaseDetailsState } from './data-access.state';
import { Injectable } from '@angular/core';
import * as CaseDetailsActions from './data-access.actions';
import * as CaseDetailsSelectors from './data-access.selectors';
import { CaseDetails, CaseEvidence, CaseFolder, CaseMemberDetails, UidModel } from '../models/case.models';
import { InviteCaseGuestsRequest, UpdateCaseRequest, CaseEvidenceRequest, NewFolderRequest } from '../models/case-request.models';
import { Observable, filter, of, switchMap, map } from 'rxjs';
import { OfficerCaseRole } from '../models/enums';
import { ActionType, CaseDetailsActionResult } from '../models/case-action-result.models';

@Injectable({
  providedIn: 'root',
})
export class CaseDetailsFacade {
  public constructor(private readonly store: Store<ICaseDetailsState>) {}

  public fetchCaseDetailsByUid(uid: string): void {
    this.store.dispatch(CaseDetailsActions.fetchCaseDetails({ uid: uid }));
  }

  public fetchCaseMembers(): void {
    this.store.dispatch(CaseDetailsActions.fetchCaseMembers());
  }

  public getCaseMembers(withoutOwner: boolean = false): Observable<CaseMemberDetails[]> {
    return this.store.select(CaseDetailsSelectors.caseMembersSelector).pipe(
      map((caseMembers) => {
        let members;

        if (withoutOwner) {
          members = caseMembers.filter((member) => member.role !== OfficerCaseRole.Owner);
        } else {
          members = [...caseMembers];
        }

        members = members.sort((a, b) => {
          if (a.firstName === null && b.firstName === null) {
            return 0;
          }
          if (a.firstName === null) {
            return -1;
          }
          if (b.firstName === null) {
            return 1;
          }

          return a.firstName.localeCompare(b.firstName);
        });

        return members;
      })
    );
  }

  public caseMembersLoading(): Observable<boolean> {
    return this.store.select(CaseDetailsSelectors.caseMembersLoadingSelector);
  }

  public getCaseDetails(): Observable<CaseDetails> {
    return this.store.select(CaseDetailsSelectors.caseDetailsSelector).pipe(filter((x): x is CaseDetails => x !== null));
  }

  public getCaseUid(): Observable<string> {
    return this.store.select(CaseDetailsSelectors.caseUidSelector).pipe(filter((x): x is string => x !== null));
  }

  public isLoading(): Observable<boolean> {
    return this.store.select(CaseDetailsSelectors.isLoading);
  }

  public addFolder(request: NewFolderRequest): void {
    this.store.dispatch(CaseDetailsActions.addFolder({ folderRequest: request }));
  }

  public addEvidenceFile(evidenceRequest: CaseEvidenceRequest, evidenceDetails: CaseEvidence): void {
    this.store.dispatch(CaseDetailsActions.addEvidenceFile({ evidenceRequest: evidenceRequest, evidenceDetails: evidenceDetails }));
  }

  public addUploadedEvidenceFile(evidence: CaseEvidence): void {
    this.store.dispatch(CaseDetailsActions.addEvidenceFileSuccess({ evidence: evidence }));
  }

  public updateCase(updatedCaseDetails: UpdateCaseRequest): void {
    this.store.dispatch(CaseDetailsActions.updateCase({ updatedCaseDetails: updatedCaseDetails }));
  }

  public addMember(member: CaseMemberDetails): void {
    this.store.dispatch(CaseDetailsActions.addCaseMember({ member: member }));
  }

  public getCaseEvidenceFiles(): Observable<CaseEvidence[]> {
    return this.store.select(CaseDetailsSelectors.caseEvidenceFilesSelector);
  }

  public getCaseEvidenceFolders(): Observable<CaseFolder[]> {
    return this.store.select(CaseDetailsSelectors.caseEvidenceFoldersSelector);
  }

  public moveFileToFolder(movedFileUid: string, destinationFolder: UidModel | null): void {
    this.store.dispatch(CaseDetailsActions.moveEvidenceFileToFolder({ movedFileUid: movedFileUid, destinationFolder: destinationFolder }));
  }

  public deleteFolder(folderUid: string): void {
    this.store.dispatch(CaseDetailsActions.deleteFolder({ folderUid: folderUid }));
  }

  public removeCaseEvidence(evidenceUid: string): void {
    this.store.dispatch(CaseDetailsActions.removeEvidence({ evidenceUid: evidenceUid }));
  }

  public removeCaseMember(memberUid: string): void {
    this.store.dispatch(CaseDetailsActions.removeMember({ memberUid: memberUid }));
  }

  public filesLoading(): Observable<boolean> {
    return this.store.select(CaseDetailsSelectors.caseEvidenceFilesLoadingSelector);
  }

  public foldersLoading(): Observable<boolean> {
    return this.store.select(CaseDetailsSelectors.caseEvidenceFoldersLoadingSelector);
  }

  public clearState(): void {
    this.store.dispatch(CaseDetailsActions.clearState());
  }

  public updateOfficerCasePermissions(role: OfficerCaseRole): void {
    this.store.dispatch(CaseDetailsActions.updateOfficerCaseRole({ role }));
  }

  public updateMember(member: CaseMemberDetails, role: OfficerCaseRole): void {
    this.store.dispatch(CaseDetailsActions.updateCaseMember({ member, role }));
  }

  public getOfficerCaseRole(): Observable<OfficerCaseRole> {
    return this.store.select(CaseDetailsSelectors.officerCaseRoleSelector).pipe(filter((x): x is OfficerCaseRole => x !== null));
  }

  public inviteGuests(request: InviteCaseGuestsRequest): void {
    this.store.dispatch(CaseDetailsActions.inviteGuests({ request }));
  }

  public caseDetailsActionResult(actionType: ActionType): Observable<CaseDetailsActionResult> {
    return this.isLoading().pipe(
      switchMap((isLoading) => {
        if (isLoading) {
          return of();
        }

        return this.store.select(CaseDetailsSelectors.actionResult).pipe(filter((x): x is CaseDetailsActionResult => !!x && x.actionType === actionType));
      })
    );
  }
}
